import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, type Webhook, type WebhookForwardResult, type WebhookForwardTarget, type WebhookRequest, type WebhookRule } from "../lib/api";
import RelativeTime from "./RelativeTime";
import { copyToClipboard } from "./Toast";
import { InlineLoading, LoadingButton } from "./Spinner";
import { useConfirm } from "./ConfirmDialog";
import CodeViewer, { detectLangFromContentType, tryPrettyJson } from "./CodeViewer";
import Markdown from "./Markdown";
import { useResizable, useMediaQuery } from "../lib/useResizable";
import { closeOpenTabFor } from "../lib/openTabs";

type Tab = "inbox" | "response" | "forward" | "security" | "docs";

// Default body for new transform editors. Documents the API + returns
// the request unchanged so the user can press Save and verify wiring.
const DEFAULT_TRANSFORM = `// Inspect or mutate the incoming request before it's forwarded.
// Return an object with any of { url, method, headers, body } to override
// the destination, or just return req to forward as-is.
//
// Available: req = {
//   method, path, subPath, query: [[k,v]…], headers: [[k,v]…],
//   body: string | null, contentType: string | null
// }
//
// Example — re-shape the JSON body before forwarding:
//   const data = JSON.parse(req.body || "{}");
//   return { body: JSON.stringify({ wrapped: data }) };
return req;
`;

export default function WebhookBuilder({ webhookId, workspaceId }: { webhookId: string; workspaceId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const detailQ = useQuery({
    queryKey: ["webhook", webhookId],
    queryFn: () => api.webhooks.get(webhookId),
  });
  const data = detailQ.data?.webhook ?? null;

  // Local editor state — initialized from the loaded webhook, then editable.
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [docs, setDocs] = useState("");
  const [respStatus, setRespStatus] = useState(200);
  const [respContentType, setRespContentType] = useState("text/plain; charset=utf-8");
  const [respBody, setRespBody] = useState("OK");
  const [forwardEnabled, setForwardEnabled] = useState(false);
  // Per-target config — each entry has its own URL, method, and
  // transform script. First slot is always present so the user has
  // somewhere to type; empty rows are dropped on save.
  const [forwardTargets, setForwardTargets] = useState<WebhookForwardTarget[]>([{ url: "", method: null, transform: null }]);
  // Security: IP CIDR allowlist (empty = wide open) + optional bearer
  // token. The token is sent in `Authorization: Bearer <token>`.
  const [allowedIps, setAllowedIps] = useState<string[]>([]);
  const [requiredAuthToken, setRequiredAuthToken] = useState<string>("");
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);

  // Tab — URL-driven so deep-linkable.
  const [sp, setSp] = useSearchParams();
  const tab: Tab = ((): Tab => {
    const q = sp.get("tab") ?? "";
    const valid: Tab[] = ["inbox", "response", "forward", "security", "docs"];
    return (valid as string[]).includes(q) ? (q as Tab) : "inbox";
  })();
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(sp);
    if (t === "inbox") next.delete("tab"); else next.set("tab", t);
    setSp(next, { replace: true });
  };

  // Pull the latest server name on every refetch — the rename flow
  // mutates server-side and invalidates this query, so a stale local
  // copy would otherwise keep the old name in the header until the
  // user navigated away.
  useEffect(() => {
    if (data?.name) setName(data.name);
  }, [data?.name]);

  // Reset local state whenever a different webhook loads.
  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setDescription(data.description ?? "");
    setDocs(data.docs ?? "");
    setRespStatus(data.resp_status);
    setRespContentType(data.resp_content_type);
    setRespBody(data.resp_body);
    setForwardEnabled(!!data.forward_enabled);
    // Server always returns a populated forward_targets array — empty
    // when nothing's configured. Seed an empty slot so the form has
    // something visible.
    setForwardTargets(data.forward_targets?.length ? data.forward_targets : [{ url: "", method: null, transform: null }]);
    setAllowedIps(data.allowed_ips ?? []);
    setRequiredAuthToken(data.required_auth_token ?? "");
    setSavedAt(data.updated_at);
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reflect the active webhook in the browser tab title.
  useEffect(() => {
    if (!name) return;
    const prev = document.title;
    document.title = `${name} · API Stash`;
    return () => { document.title = prev; };
  }, [name]);

  const cleanTargets = useMemo<WebhookForwardTarget[]>(() => forwardTargets
    .map((t) => ({
      url: t.url.trim(),
      method: t.method || null,
      transform: t.transform && t.transform.trim() ? t.transform : null,
    }))
    .filter((t) => t.url.length > 0)
    .slice(0, 3),
  [forwardTargets]);
  const savedTargets = useMemo<WebhookForwardTarget[]>(() => (data?.forward_targets ?? []).map((t) => ({
    url: t.url,
    method: t.method,
    transform: t.transform,
  })), [data?.forward_targets]);
  const cleanAllowedIps = useMemo(() => allowedIps.map((s) => s.trim()).filter(Boolean), [allowedIps]);
  const savedAllowedIps = useMemo(() => (data?.allowed_ips ?? []).map((s) => s.trim()).filter(Boolean), [data?.allowed_ips]);
  const trimmedToken = requiredAuthToken.trim();
  const isDirty = useMemo(() => {
    if (!data) return false;
    return (
      data.name !== name ||
      (data.description ?? "") !== description ||
      (data.docs ?? "") !== docs ||
      data.resp_status !== respStatus ||
      data.resp_content_type !== respContentType ||
      data.resp_body !== respBody ||
      !!data.forward_enabled !== forwardEnabled ||
      JSON.stringify(savedTargets) !== JSON.stringify(cleanTargets) ||
      JSON.stringify(savedAllowedIps) !== JSON.stringify(cleanAllowedIps) ||
      (data.required_auth_token ?? "") !== trimmedToken
    );
  }, [data, name, description, docs, respStatus, respContentType, respBody, forwardEnabled, savedTargets, cleanTargets, savedAllowedIps, cleanAllowedIps, trimmedToken]);

  const save = useMutation({
    mutationFn: () => api.webhooks.update(webhookId, {
      name, description, docs,
      resp_status: respStatus,
      resp_content_type: respContentType,
      resp_body: respBody,
      forward_enabled: forwardEnabled,
      forward_targets: cleanTargets,
      allowed_ips: cleanAllowedIps,
      required_auth_token: trimmedToken || null,
    }),
    onSuccess: () => {
      setSavedAt(new Date().toISOString());
      qc.invalidateQueries({ queryKey: ["webhook", webhookId] });
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["webhooks", data.collection_id] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.webhooks.remove(webhookId),
    onSuccess: () => {
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["webhooks", data.collection_id] });
      const { search, nextActive } = closeOpenTabFor(workspaceId, `w:${webhookId}`);
      const path = nextActive ? routeForTabId(nextActive) : "/";
      window.location.assign(path + search);
    },
  });

  const isDesktopSplit = useMediaQuery("(min-width: 768px)");
  const { size: editorWidth, dragProps: editorDragX } = useResizable({
    axis: "x", initial: 480, min: 100, max: 1600, storageKey: "rest.webhookBuilder.editorWidth",
  });
  const { size: editorHeight, dragProps: editorDragY } = useResizable({
    axis: "y", initial: 360, min: 100, max: 1200, storageKey: "rest.webhookBuilder.editorHeight",
  });
  const editorPaneStyle = isDesktopSplit ? { width: editorWidth } : { height: editorHeight };

  if (detailQ.isLoading) {
    return <div className="grid h-full place-items-center"><InlineLoading label="Loading webhook…" /></div>;
  }
  if (!data) {
    return <div className="grid h-full place-items-center muted">Webhook not found.</div>;
  }

  const copy = (text: string, label: string) => { copyToClipboard(text, label); };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="border-b border-border bg-surface px-3 py-1.5 flex items-center gap-2">
        <div className="flex-1 min-w-0 truncate text-sm font-medium text-text" title={name}>{name || "Untitled webhook"}</div>
        {isDirty && <span className="chip bg-warn/15 text-warn">Unsaved</span>}
        {!isDirty && savedAt && (
          <RelativeTime iso={savedAt} className="text-xs muted-strong" prefix="Saved " />
        )}
        {copyToast && <span className="chip bg-good/15 text-good">{copyToast}</span>}
        <LoadingButton
          className="btn"
          pending={save.isPending}
          disabled={!isDirty}
          onClick={() => save.mutate()}
        >Save</LoadingButton>
        <button
          type="button"
          className="btn"
          title="Send a test POST"
          onClick={async () => {
            const ok = await confirm({
              title: "Send a test request?",
              description: <>This sends <span className="mono">POST {data.url}</span> with body <span className="mono">{`{"hello":"world"}`}</span>. The capture will show up in the Inbox tab.</>,
            });
            if (!ok) return;
            try {
              await fetch(data.url, { method: "POST", headers: { "Content-Type": "application/json" }, body: '{"hello":"world"}' });
              setCopyToast("Sent");
              setTimeout(() => setCopyToast(null), 2_000);
            } catch (e) {
              setCopyToast(`Send failed: ${(e as Error).message}`);
              setTimeout(() => setCopyToast(null), 3_000);
            }
          }}
        >Send a test</button>
        <button
          type="button"
          onClick={async () => {
            const ok = await confirm({
              title: `Delete "${data.name}"?`,
              description: "This permanently removes the webhook and all its captured requests.",
              danger: true,
            });
            if (ok) remove.mutate();
          }}
          className="btn text-danger"
          aria-label="Delete webhook"
        >Delete</button>
      </div>

      {/* URL banner — same vertical rhythm as the request builder's
          URL bar so the two views feel consistent when you swap between
          tabs. */}
      <div className="border-b border-border bg-surface-2 px-3 py-3 flex items-stretch gap-2">
        <span aria-hidden="true" className="text-accent shrink-0 self-center"><BoltIcon /></span>
        {/* Match the Copy URL button: same padding/border/font-size so
            the two read as a matched pair instead of a small URL next
            to a tall button. */}
        <code className="mono flex-1 min-w-0 truncate text-[13px] px-3 py-[6px] rounded border border-border bg-bg flex items-center" title={data.url}>
          {data.url}
          <span className="muted">{"/<any/path>"}</span>
        </code>
        <button
          type="button"
          className="btn shrink-0"
          onClick={() => copy(data.url, "Copied URL")}
          title="Copy URL"
        >Copy URL</button>
      </div>

      {/* Tabs */}
      <div className="border-b border-border bg-surface px-3 overflow-x-auto">
        <Tabs current={tab} onChange={setTab} requestCount={data.request_count} forwardEnabled={forwardEnabled} locked={cleanAllowedIps.length > 0 || !!trimmedToken} />
      </div>

      <div className="flex-1 min-h-0 flex flex-col md:flex-row">
        {(() => {
          // Most tabs are full-width — the inbox lays out its own split.
          const fullWidth = true;
          return (
            <div className={"min-h-0 overflow-auto scroll-pane p-3 " + (fullWidth ? "flex-1 min-w-0" : "shrink-0")} style={fullWidth ? undefined : editorPaneStyle}>
              {tab === "inbox" && <InboxTab webhookId={webhookId} webhookUrl={data.url} />}
              {tab === "response" && (
                <ResponseTab
                  webhookId={webhookId}
                  status={respStatus} setStatus={setRespStatus}
                  contentType={respContentType} setContentType={setRespContentType}
                  body={respBody} setBody={setRespBody}
                />
              )}
              {tab === "forward" && (
                <ForwardTab
                  enabled={forwardEnabled} setEnabled={setForwardEnabled}
                  targets={forwardTargets} setTargets={setForwardTargets}
                />
              )}
              {tab === "security" && (
                <SecurityTab
                  allowedIps={allowedIps} setAllowedIps={setAllowedIps}
                  authToken={requiredAuthToken} setAuthToken={setRequiredAuthToken}
                />
              )}
              {tab === "docs" && (
                <DocsEditor
                  description={description} setDescription={setDescription}
                  docs={docs} setDocs={setDocs}
                />
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// Map a tab id (`r:abc` / `w:abc`) back to its route.
function routeForTabId(id: string): string {
  if (id.startsWith("w:")) return `/w/${id.slice(2)}`;
  if (id.startsWith("r:")) return `/r/${id.slice(2)}`;
  return `/r/${id}`;
}

function Tabs({ current, onChange, requestCount, forwardEnabled, locked }: {
  current: Tab; onChange: (t: Tab) => void; requestCount: number; forwardEnabled: boolean; locked: boolean;
}) {
  const items: Array<{ id: Tab; label: string; count?: number; dot?: boolean; lock?: boolean }> = [
    { id: "inbox", label: "Inbox", count: requestCount },
    { id: "response", label: "Response" },
    { id: "forward", label: "Forward", dot: forwardEnabled },
    { id: "security", label: "Security", lock: locked },
    { id: "docs", label: "Docs" },
  ];
  return (
    <div className="flex gap-1">
      {items.map((t) => {
        const active = t.id === current;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 " +
              (active ? "border-accent text-text" : "border-transparent text-text-dim hover:text-text")
            }
          >
            {t.label}
            {!!t.count && (
              <span className="ml-1 chip bg-surface-3 muted-strong">{t.count}</span>
            )}
            {t.dot && <span className="h-1.5 w-1.5 rounded-full bg-good" title="Forwarding enabled" />}
            {t.lock && (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true" aria-label="Locked">
                <path d="M7 11V8a5 5 0 0110 0v3M5 11h14v9H5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Inbox tab ──────────────────────────────────────────────────────────

function InboxTab({ webhookId, webhookUrl }: { webhookId: string; webhookUrl: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  // Resizable split between the captured list and the detail panel.
  // Mobile stays stacked (no horizontal split), so the width is only
  // applied above the desktop breakpoint.
  const isDesktopWidth = useMediaQuery("(min-width: 768px)");
  const { size: listWidth, dragProps: listDrag } = useResizable({
    axis: "x", initial: 320, min: 200, max: 720, storageKey: "rest.webhook.inboxListWidth",
  });
  const reqsQ = useQuery({
    queryKey: ["webhook-requests", webhookId],
    queryFn: () => api.webhooks.requests(webhookId),
    refetchInterval: 2_000,
    // Tagged so LoadingBar skips this poll — otherwise the top
    // indicator blinks every 2s while the Inbox tab is open.
    meta: { background: true },
  });
  const requests = reqsQ.data?.requests ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!requests.length) { setSelectedId(null); return; }
    if (!selectedId || !requests.find((r) => r.id === selectedId)) {
      setSelectedId(requests[0].id);
    }
  }, [requests, selectedId]);

  const selected = requests.find((r) => r.id === selectedId) ?? null;

  // Whenever the captured list changes, the request_count + last_request_at
  // shown in the tab pill AND in the sidebar row need to refresh too.
  // Bust all three so the UI doesn't show stale counters after delete /
  // clear.
  const invalidateCounts = () => {
    qc.invalidateQueries({ queryKey: ["webhook-requests", webhookId] });
    qc.invalidateQueries({ queryKey: ["webhook", webhookId] });
    qc.invalidateQueries({ queryKey: ["webhooks"] });
  };
  const clear = useMutation({
    mutationFn: () => api.webhooks.clearRequests(webhookId),
    onSuccess: invalidateCounts,
  });
  const delOne = useMutation({
    mutationFn: (reqId: string) => api.webhooks.deleteRequest(reqId),
    onSuccess: invalidateCounts,
  });

  return (
    <div className="h-full flex flex-col md:flex-row min-h-0 -m-3">
      {/* Left pane: captured list. Styled to match the workspace
          sidebar — no rounded card around the list, just a border-r
          separator and a thin drag handle riding over the edge. */}
      <aside
        className="relative shrink-0 flex flex-col min-h-0 border-r border-border bg-surface md:bg-transparent"
        style={isDesktopWidth ? { width: listWidth } : undefined}
      >
        <div className="h-9 px-3 flex items-center justify-between border-b border-border">
          <div className="text-xs uppercase tracking-wider muted-strong font-semibold flex items-center gap-1.5">
            <span className="pulse-dot" title="Listening live" /> Captured
          </div>
          <button
            className="text-xs muted hover:text-text disabled:opacity-50"
            disabled={!requests.length}
            onClick={async () => {
              if (await confirm({ title: "Clear all captured requests?", description: "This permanently deletes every captured request for this webhook.", danger: true })) clear.mutate();
            }}
          >Clear</button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto scroll-pane">
          {requests.length === 0 ? (
            <WaitingState webhookUrl={webhookUrl} />
          ) : (
            <ul className="px-2 py-2 space-y-0.5">
              {requests.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={
                      "group flex items-center gap-2.5 w-full min-w-0 px-2 py-1.5 rounded text-sm text-left transition-colors " +
                      (r.id === selectedId
                        ? "bg-accent-soft text-accent-bright"
                        : "text-text-dim hover:text-text hover:bg-surface-2")
                    }
                  >
                    <span className={"method-tag shrink-0 " + r.method.toLowerCase()}>{r.method}</span>
                    <span className="mono min-w-0 flex-1 truncate text-xs">{r.path}</span>
                    {r.forward_error || r.transform_error ? (
                      <span className="chip bg-danger/15 text-danger shrink-0" title={r.forward_error ?? r.transform_error ?? ""}>!</span>
                    ) : r.forward_status != null ? (
                      <span className={"chip shrink-0 " + statusClass(r.forward_status)}>{r.forward_status}</span>
                    ) : null}
                    <RelativeTime iso={r.received_at} className="text-[11px] muted-strong shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Resize handle riding the border-r edge — same pattern as the
            workspace sidebar resizer. Only mounted on desktop. */}
        {isDesktopWidth && (
          <div
            {...listDrag}
            aria-label="Resize captured / detail"
            className="hidden md:block absolute top-0 right-[-3px] bottom-0 w-1.5 cursor-col-resize bg-transparent hover:bg-accent active:bg-accent-bright transition-colors z-10"
          />
        )}
      </aside>
      <div className="flex-1 min-w-0 min-h-0 overflow-auto scroll-pane p-3">
        {selected ? (
          <CapturedRequestPanel
            request={selected}
            webhookUrl={webhookUrl}
            onDelete={async () => {
              if (await confirm({ title: "Delete request?", description: "This permanently deletes this captured request.", danger: true })) delOne.mutate(selected.id);
            }}
          />
        ) : (
          <div className="grid h-full place-items-center muted text-sm">Select a request to inspect it.</div>
        )}
      </div>
    </div>
  );
}

function CapturedRequestPanel({ request, webhookUrl, onDelete }: { request: WebhookRequest; webhookUrl: string; onDelete: () => void }) {
  type Sub = "request" | "forward";
  const [sub, setSub] = useState<Sub>("request");
  const lang = detectLangFromContentType(request.content_type);
  // tryPrettyJson returns the original string when it can't parse — so a
  // value-equality check tells us whether the body actually was JSON.
  const prettyText = request.body ? tryPrettyJson(request.body) : "";
  const isJson = !!request.body && prettyText !== request.body;
  const showBody = request.body && request.body.length > 0;
  const legs = request.forward_results ?? [];
  const hasForward = legs.length > 0 || request.transform_error;
  const anyOk = legs.some((l) => l.status != null && !l.error);
  const anyErr = legs.some((l) => l.error) || !!request.transform_error;
  // Full URL that the inbound caller hit. webhookUrl already ends with
  // the token (no trailing slash); we just append the captured sub-path
  // and the original query string so the curl reproducer is faithful.
  const fullUrl = (() => {
    let qs = "";
    if (request.query.length) {
      const sp = new URLSearchParams();
      for (const [k, v] of request.query) sp.append(k, v);
      qs = `?${sp.toString()}`;
    }
    const base = webhookUrl.replace(/\/$/, "");
    const path = request.path === "/" ? "" : request.path;
    return `${base}${path}${qs}`;
  })();
  const curl = buildCurl(request, fullUrl);
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex min-w-0 items-center gap-2">
          <span className={"method-tag " + request.method.toLowerCase()}>{request.method}</span>
          <span className="mono truncate text-sm">{request.path}</span>
        </div>
        <div className="flex items-center gap-1">
          <CopyButton label="Copy URL" payload={fullUrl} />
          <CopyButton label="Copy as curl" payload={curl} />
          <button type="button" className="btn text-danger" onClick={onDelete}>Delete</button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
        <Meta label="Received" value={new Date(request.received_at).toLocaleString()} />
        <Meta label="Source IP" value={request.remote_ip ?? "—"} mono />
        <Meta label="Size" value={`${request.body_size} bytes${request.truncated ? " (truncated)" : ""}`} />
        <Meta label="Content-Type" value={request.content_type ?? "—"} mono />
        <Meta label="Host" value={request.host ?? "—"} mono />
        <Meta label="UA" value={request.user_agent ?? "—"} mono />
      </div>

      {hasForward && (
        <div className="border-b border-border flex gap-1">
          <SubTab active={sub === "request"} onClick={() => setSub("request")}>Request</SubTab>
          <SubTab active={sub === "forward"} onClick={() => setSub("forward")}>
            Forward {legs.length > 1 ? <span className="chip bg-surface-3 muted-strong">{legs.length}</span> : null}
            {anyOk && legs.length === 1 && legs[0].status != null && (
              <span className={"chip " + statusClass(legs[0].status)}>{legs[0].status}</span>
            )}
            {anyErr && (
              <span className="chip bg-danger/15 text-danger">error</span>
            )}
          </SubTab>
        </div>
      )}

      {(!hasForward || sub === "request") && (
        <div className="space-y-3">
          {request.query.length > 0 && (
            <div>
              <div className="label mb-1">Query</div>
              <PairTable pairs={request.query} />
            </div>
          )}
          <div>
            <div className="label mb-1 flex items-center gap-2">
              <span>Headers <span className="muted-strong">({request.headers.length})</span></span>
              {request.headers.length > 0 && (
                <CopyButton
                  label="Copy"
                  size="xs"
                  payload={JSON.stringify(Object.fromEntries(request.headers), null, 2)}
                />
              )}
            </div>
            <PairTable pairs={request.headers} />
          </div>
          {showBody && (
            <div>
              <div className="label mb-1 flex items-center gap-2">
                <span>Body{isJson ? " (JSON)" : ""}</span>
                <CopyButton
                  label="Copy"
                  size="xs"
                  payload={isJson ? prettyText : (request.body ?? "")}
                />
              </div>
              <CodeViewer value={isJson ? prettyText : (request.body ?? "")} lang={isJson ? "json" : lang} />
            </div>
          )}
        </div>
      )}

      {hasForward && sub === "forward" && (
        <ForwardResultPanel request={request} />
      )}
    </div>
  );
}

function ForwardResultPanel({ request }: { request: WebhookRequest }) {
  const legs = request.forward_results ?? [];
  // Single-leg view stays flat. Multi-leg view collapses each leg into an
  // accordion so the user can drill in independently.
  const [openIdx, setOpenIdx] = useState<number>(0);
  return (
    <div className="space-y-3">
      {request.transform_error && (
        <div className="rounded border border-danger/30 bg-danger/[0.06] p-2 text-xs">
          <div className="font-semibold text-danger mb-1">Transform error</div>
          <pre className="mono whitespace-pre-wrap">{request.transform_error}</pre>
        </div>
      )}
      {request.transformed_request != null && (
        <div>
          <div className="label mb-1">Outbound request</div>
          <CodeViewer value={JSON.stringify(request.transformed_request, null, 2)} lang="json" />
        </div>
      )}
      {legs.length === 1 && <ForwardLegBody leg={legs[0]} index={0} />}
      {legs.length > 1 && (
        <div className="space-y-1.5">
          {legs.map((leg, i) => {
            const open = i === openIdx;
            return (
              <div key={i} className="rounded border border-border">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-surface-2"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                >
                  <span className="mono text-[10px] muted-strong w-4 text-right shrink-0">{i + 1}</span>
                  <span className="mono text-xs flex-1 min-w-0 truncate">{leg.url ?? "—"}</span>
                  {leg.duration_ms != null && (
                    <span className="muted-strong text-[11px] shrink-0">{leg.duration_ms} ms</span>
                  )}
                  {leg.error ? (
                    <span className="chip bg-danger/15 text-danger shrink-0">error</span>
                  ) : leg.status != null ? (
                    <span className={"chip shrink-0 " + statusClass(leg.status)}>{leg.status}</span>
                  ) : (
                    <span className="chip bg-surface-3 muted-strong shrink-0">pending</span>
                  )}
                  <span className="muted text-xs shrink-0">{open ? "▾" : "▸"}</span>
                </button>
                {open && (
                  <div className="border-t border-border p-2.5">
                    <ForwardLegBody leg={leg} index={i} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ForwardLegBody({ leg, index }: { leg: WebhookForwardResult; index: number }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
        {leg.url && <Meta label={`Destination ${index + 1}`} value={leg.url} mono />}
        <Meta label="Status" value={leg.status != null ? String(leg.status) : "—"} />
        <Meta label="Duration" value={leg.duration_ms != null ? `${leg.duration_ms} ms` : "—"} />
      </div>
      {leg.error && (
        <div className="rounded border border-danger/30 bg-danger/[0.06] p-2 text-xs">
          <div className="font-semibold text-danger mb-1">Forward error</div>
          <pre className="mono whitespace-pre-wrap">{leg.error}</pre>
        </div>
      )}
      {leg.headers && (
        <div>
          <div className="label mb-1">Response headers <span className="muted-strong">({leg.headers.length})</span></div>
          <PairTable pairs={leg.headers} />
        </div>
      )}
      {leg.body != null && (
        <div>
          <div className="label mb-1 flex items-center gap-2">
            <span>Response body</span>
            <CopyButton label="Copy" size="xs" payload={leg.body} />
          </div>
          <CodeViewer value={leg.body} lang="plain" />
        </div>
      )}
    </div>
  );
}

function PairTable({ pairs }: { pairs: [string, string][] }) {
  if (!pairs.length) return <p className="text-xs muted italic">None.</p>;
  return (
    <table className="w-full text-xs border border-border rounded">
      <tbody>
        {pairs.map(([k, v], i) => (
          <tr key={i} className="border-b border-border align-top last:border-0">
            <td className="mono w-1/3 py-1.5 px-2 font-medium text-text-dim">{k}</td>
            <td className="mono py-1.5 px-2 break-all">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Meta({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="muted-strong">{label}</div>
      <div className={"truncate " + (mono ? "mono" : "")} title={value}>{value}</div>
    </div>
  );
}

function SubTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-3 py-2 text-sm border-b-2 -mb-px flex items-center gap-2 " +
        (active ? "border-accent text-text font-medium" : "border-transparent text-text-dim hover:text-text")
      }
    >{children}</button>
  );
}

function WaitingState({ webhookUrl }: { webhookUrl: string }) {
  const example = `curl -X POST ${webhookUrl} \\\n  -H 'Content-Type: application/json' \\\n  -d '{"hello":"world"}'`;
  return (
    <div className="p-3 text-xs space-y-2">
      <p className="muted flex items-center gap-1.5"><span className="pulse-dot" /> Waiting for requests…</p>
      <div className="flex items-center justify-between gap-2">
        <p className="muted">Try it from your terminal:</p>
        <CopyButton label="Copy curl" payload={example} size="xs" />
      </div>
      <pre className="rounded border border-border bg-surface-2 p-2 mono whitespace-pre-wrap">{example}</pre>
    </div>
  );
}

// ── Response tab ───────────────────────────────────────────────────────

function ResponseTab({
  webhookId, status, setStatus, contentType, setContentType, body, setBody,
}: {
  webhookId: string;
  status: number; setStatus: (n: number) => void;
  contentType: string; setContentType: (s: string) => void;
  body: string; setBody: (s: string) => void;
}) {
  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-sm font-semibold mb-1">Default response</h3>
        <p className="text-xs muted">Sent when no rule below matches the incoming request.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[120px_minmax(0,1fr)] gap-3 items-start">
        <label className="label pt-1">Status</label>
        <input
          className="input mono w-28"
          value={String(status)}
          onChange={(e) => setStatus(parseInt(e.target.value.replace(/[^0-9]/g, ""), 10) || 200)}
        />
        <label className="label pt-1">Content-Type</label>
        <input
          className="input mono"
          value={contentType}
          onChange={(e) => setContentType(e.target.value)}
        />
        <label className="label pt-1">Body</label>
        <textarea
          className="input input-mono min-h-[120px]"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          spellCheck={false}
        />
      </div>

      <WebhookRules webhookId={webhookId} />
    </div>
  );
}

function WebhookRules({ webhookId }: { webhookId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const rulesQ = useQuery({
    queryKey: ["webhook-rules", webhookId],
    queryFn: () => api.webhooks.rules(webhookId),
  });
  const rules = rulesQ.data?.rules ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["webhook-rules", webhookId] });

  const [editing, setEditing] = useState<WebhookRule | null>(null);
  const [draftNew, setDraftNew] = useState(false);

  const save = useMutation({
    mutationFn: async (r: WebhookRule) => {
      const input = { method: r.method, path: r.path, resp_status: r.resp_status, resp_headers: r.resp_headers, resp_body: r.resp_body };
      if (r.id) await api.webhooks.updateRule(webhookId, r.id, input);
      else await api.webhooks.addRule(webhookId, input);
    },
    onSuccess: () => { setEditing(null); setDraftNew(false); invalidate(); },
  });
  const del = useMutation({
    mutationFn: (ruleId: string) => api.webhooks.deleteRule(webhookId, ruleId),
    onSuccess: invalidate,
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.webhooks.reorderRules(webhookId, ids),
    onSuccess: invalidate,
  });
  const move = (idx: number, dir: -1 | 1) => {
    const ids = rules.map((r) => r.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    reorder.mutate(ids);
  };

  return (
    <div className="space-y-2 border-t border-border pt-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Response rules</h3>
          <p className="text-xs muted mt-1">Match by method + path. First match wins. No match → default response above.</p>
        </div>
        <button className="btn text-xs" disabled={draftNew} onClick={() => { setEditing(null); setDraftNew(true); }}>＋ Add rule</button>
      </div>
      <ul className="space-y-1">
        {rules.map((rule, i) => editing?.id === rule.id ? (
          <RuleEditor
            key={rule.id}
            initial={editing}
            onCancel={() => setEditing(null)}
            onSave={(r) => save.mutate({ ...r, id: rule.id, sort_order: rule.sort_order })}
            pending={save.isPending}
          />
        ) : (
          <li key={rule.id} className="flex items-center gap-2 rounded border border-border bg-surface-2 px-2 py-2">
            <div className="flex flex-col text-text-faint">
              <button type="button" className="px-1 leading-none hover:text-text disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} title="Up">↑</button>
              <button type="button" className="px-1 leading-none hover:text-text disabled:opacity-30" disabled={i === rules.length - 1} onClick={() => move(i, 1)} title="Down">↓</button>
            </div>
            <button
              type="button"
              className="flex flex-1 min-w-0 items-center gap-2 text-left"
              onClick={() => setEditing(rule)}
            >
              <span className={"method-tag shrink-0 " + rule.method.toLowerCase()}>{rule.method === "ANY" ? "ANY" : rule.method}</span>
              <span className="mono min-w-0 flex-1 truncate text-xs">{rule.path || <span className="muted italic">any path</span>}</span>
              <span className="muted-strong shrink-0">→</span>
              <span className={"chip shrink-0 " + statusClass(rule.resp_status)}>{rule.resp_status}</span>
            </button>
            <button
              type="button"
              className="btn text-danger"
              onClick={async () => {
                if (await confirm({ title: "Delete rule?", danger: true })) del.mutate(rule.id);
              }}
            >Delete</button>
          </li>
        ))}
        {draftNew && (
          <RuleEditor
            initial={{ id: "", sort_order: rules.length, method: "ANY", path: "", resp_status: 200, resp_headers: [["Content-Type", "application/json"]], resp_body: '{ "ok": true }' }}
            onCancel={() => setDraftNew(false)}
            onSave={(r) => save.mutate(r)}
            pending={save.isPending}
            isNew
          />
        )}
        {!draftNew && rules.length === 0 && (
          <li className="empty-card text-xs">No rules yet — every request gets the default response above.</li>
        )}
      </ul>
    </div>
  );
}

function RuleEditor({ initial, onCancel, onSave, pending, isNew }: {
  initial: WebhookRule;
  onCancel: () => void;
  onSave: (r: WebhookRule) => void;
  pending: boolean;
  isNew?: boolean;
}) {
  const [draft, setDraft] = useState<WebhookRule>(initial);
  const set = (p: Partial<WebhookRule>) => setDraft({ ...draft, ...p });
  return (
    <div className="space-y-3 rounded border border-accent bg-surface-2 p-3">
      <div className="space-y-2">
        <div className="label">When request matches</div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select className="input input-mono w-36" value={draft.method} onChange={(e) => set({ method: e.target.value })}>
            {["ANY", "GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <span className="muted">on path</span>
          <input
            className="input input-mono min-w-0 flex-1"
            value={draft.path}
            onChange={(e) => set({ path: e.target.value })}
            placeholder="/users/*  (leave blank for any path)"
          />
        </div>
      </div>
      <div className="space-y-2 border-t border-border pt-3">
        <div className="label">Respond with</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            className="input mono w-24"
            value={String(draft.resp_status)}
            onChange={(e) => set({ resp_status: parseInt(e.target.value.replace(/[^0-9]/g, ""), 10) || 200 })}
            placeholder="200"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <div className="text-[11px] muted-strong">Headers</div>
            <button type="button" className="btn text-xs" onClick={() => set({ resp_headers: [...draft.resp_headers, ["", ""]] })}>＋ Header</button>
          </div>
          {draft.resp_headers.map(([k, v], i) => (
            <div key={i} className="flex items-center gap-2 mt-1">
              <input className="input input-mono" value={k} onChange={(e) => set({ resp_headers: draft.resp_headers.map((p, idx) => idx === i ? [e.target.value, p[1]] : p) })} placeholder="Content-Type" />
              <input className="input input-mono" value={v} onChange={(e) => set({ resp_headers: draft.resp_headers.map((p, idx) => idx === i ? [p[0], e.target.value] : p) })} placeholder="application/json" />
              <button type="button" className="btn text-danger" onClick={() => set({ resp_headers: draft.resp_headers.filter((_, idx) => idx !== i) })}>✕</button>
            </div>
          ))}
        </div>
        <div>
          <div className="text-[11px] muted-strong">Body</div>
          <textarea className="input input-mono min-h-[80px]" value={draft.resp_body} onChange={(e) => set({ resp_body: e.target.value })} />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
        <button className="btn" onClick={onCancel}>Cancel</button>
        <LoadingButton className="btn" pending={pending} onClick={() => onSave(draft)}>{isNew ? "Add rule" : "Save rule"}</LoadingButton>
      </div>
    </div>
  );
}

// ── Forward tab ────────────────────────────────────────────────────────

const MAX_FORWARD_TARGETS = 3;
const ALLOWED_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];

function ForwardTab({
  enabled, setEnabled, targets, setTargets,
}: {
  enabled: boolean; setEnabled: (b: boolean) => void;
  targets: WebhookForwardTarget[]; setTargets: (next: WebhookForwardTarget[]) => void;
}) {
  const slots = targets.length === 0
    ? [{ url: "", method: null, transform: null } as WebhookForwardTarget]
    : targets.slice(0, MAX_FORWARD_TARGETS);
  const update = (idx: number, patch: Partial<WebhookForwardTarget>) => {
    const next = slots.slice();
    next[idx] = { ...next[idx], ...patch };
    setTargets(next);
  };
  const removeSlot = (idx: number) => {
    const next = slots.slice();
    next.splice(idx, 1);
    if (next.length === 0) next.push({ url: "", method: null, transform: null });
    setTargets(next);
  };
  const addSlot = () => {
    if (slots.length >= MAX_FORWARD_TARGETS) return;
    setTargets([...slots, { url: "", method: null, transform: null }]);
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex items-start gap-3">
        <input
          id="forward-enabled"
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="h-4 w-4 mt-1"
        />
        <label htmlFor="forward-enabled" className="flex-1">
          <div className="text-sm font-medium">Forward to one or more destinations</div>
          <p className="text-xs muted mt-0.5">
            When enabled, every captured request is also sent — in parallel — to each destination below. Each destination has its own method and transform script, so you can fan out to wildly different downstream services. The inbound caller sees the response configured in the <b>Response</b> tab; forwarding happens asynchronously and stores every leg in the Inbox.
          </p>
        </label>
      </div>

      <div className={(enabled ? "" : "opacity-60 pointer-events-none ") + "space-y-3"}>
        <div className="flex items-center justify-between">
          <label className="label">
            Destinations <span className="muted">({slots.filter((t) => t.url.trim()).length}/{MAX_FORWARD_TARGETS})</span>
          </label>
          <button
            type="button"
            className="btn text-xs"
            onClick={addSlot}
            disabled={slots.length >= MAX_FORWARD_TARGETS}
            title={slots.length >= MAX_FORWARD_TARGETS ? `Up to ${MAX_FORWARD_TARGETS} destinations` : "Add another destination"}
          >+ Add destination</button>
        </div>
        <div className="space-y-3">
          {slots.map((t, idx) => (
            <ForwardTargetCard
              key={idx}
              index={idx}
              target={t}
              canRemove={slots.length > 1}
              onChange={(patch) => update(idx, patch)}
              onRemove={() => removeSlot(idx)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ForwardTargetCard({ index, target, canRemove, onChange, onRemove }: {
  index: number;
  target: WebhookForwardTarget;
  canRemove: boolean;
  onChange: (patch: Partial<WebhookForwardTarget>) => void;
  onRemove: () => void;
}) {
  // Transform editor is collapsed by default — most destinations don't
  // need one. Auto-open when the user has typed something so they can
  // see what they're editing.
  const [showTransform, setShowTransform] = useState(() => !!target.transform && target.transform.trim().length > 0);
  const hasTransform = !!target.transform && target.transform.trim().length > 0;
  const transformLines = target.transform ? target.transform.split("\n").length : 0;
  const filled = !!target.url.trim();
  return (
    <div
      className={
        "rounded-lg border bg-surface-2/40 " +
        (filled ? "border-border" : "border-border/60")
      }
    >
      {/* Card header — title + chips that summarize the destination,
          plus the remove control. Keeps the inputs below uncluttered. */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/70">
        <span className="h-5 w-5 grid place-items-center rounded-full bg-accent-soft text-accent-bright text-[11px] font-semibold shrink-0">
          {index + 1}
        </span>
        <span className="text-sm font-medium text-text">Destination {index + 1}</span>
        <span className="muted-strong text-[11px] hidden sm:inline">
          · {target.method ?? "method follows inbound"} · transform {hasTransform ? `${transformLines}L` : "off"}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            title="Remove this destination"
            aria-label={`Remove destination ${index + 1}`}
            className="ml-auto h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-3 shrink-0"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      <div className="p-3 space-y-3">
        {/* URL — full width, room to breathe. */}
        <div className="space-y-1">
          <label className="label">URL</label>
          <input
            className="input mono w-full"
            value={target.url}
            onChange={(e) => onChange({ url: e.target.value })}
            placeholder="https://api.example.com/webhook"
            spellCheck={false}
            autoComplete="off"
          />
        </div>

        {/* Method — its own row so it isn't fighting the URL for space.
            Capped narrow because it's a single dropdown. */}
        <div className="space-y-1">
          <label className="label">Method</label>
          <select
            className="input mono w-48"
            value={target.method ?? ""}
            onChange={(e) => onChange({ method: e.target.value || null })}
            aria-label={`Destination ${index + 1} method`}
          >
            <option value="">Same as incoming</option>
            {ALLOWED_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Transform — disclosure with a primary affordance + summary. */}
        <div className="rounded border border-border bg-bg/40">
          <button
            type="button"
            onClick={() => setShowTransform((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left"
            aria-expanded={showTransform}
          >
            <span className="text-text-faint shrink-0">{showTransform ? "▾" : "▸"}</span>
            <span className="text-sm font-medium">Transform</span>
            <span className="text-[11px] muted-strong">
              {hasTransform
                ? `${transformLines} line${transformLines === 1 ? "" : "s"} of JavaScript`
                : "Not set — incoming request forwarded as-is"}
            </span>
            <span className="ml-auto text-[11px] text-text-faint">{showTransform ? "Hide" : "Edit"}</span>
          </button>
          {showTransform && (
            <div className="border-t border-border px-3 py-3 space-y-2">
              <p className="text-[11px] muted">
                JavaScript function body. Receives <code className="mono">req</code> (the incoming request) and returns an object with any of <code className="mono">url</code>, <code className="mono">method</code>, <code className="mono">headers</code>, <code className="mono">body</code> to override. Sandboxed; 1.5s timeout. Runs per-destination so each leg can rewrite independently.
              </p>
              <textarea
                className="input input-mono w-full min-h-[180px]"
                value={target.transform ?? ""}
                onChange={(e) => onChange({ transform: e.target.value || null })}
                spellCheck={false}
                placeholder={DEFAULT_TRANSFORM}
              />
              <div className="flex items-center justify-end gap-2">
                {hasTransform && (
                  <button
                    type="button"
                    className="btn text-xs"
                    onClick={() => onChange({ transform: null })}
                  >Clear</button>
                )}
                <button
                  type="button"
                  className="btn text-xs"
                  onClick={() => onChange({ transform: DEFAULT_TRANSFORM })}
                  disabled={hasTransform}
                >Insert template</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Security tab ───────────────────────────────────────────────────────

// Client-side validation mirroring the server's isValidCidrOrIp. We
// keep it lenient on incomplete input (don't yell while the user is
// still typing) but catch obviously bogus values.
function ipValidity(value: string): { ok: boolean; reason?: string } {
  const v = value.trim();
  if (!v) return { ok: true }; // empty row, ignored on save
  const slash = v.indexOf("/");
  const base = slash < 0 ? v : v.slice(0, slash);
  const bitsStr = slash < 0 ? null : v.slice(slash + 1);
  if (slash >= 0) {
    if (!/^\d{1,3}$/.test(bitsStr!)) return { ok: false, reason: "prefix bits must be a number" };
    const n = parseInt(bitsStr!, 10);
    if (n < 0 || n > 128) return { ok: false, reason: "prefix bits out of range" };
  }
  // IPv4: must be four octets of 0-255.
  if (base.includes(".") && !base.includes(":")) {
    const parts = base.split(".");
    if (parts.length !== 4) return { ok: false, reason: "IPv4 needs four octets" };
    for (const p of parts) {
      if (!/^\d+$/.test(p)) return { ok: false, reason: `"${p}" isn't a number` };
      const n = parseInt(p, 10);
      if (n < 0 || n > 255) return { ok: false, reason: `${n} is out of 0–255` };
    }
    if (bitsStr && parseInt(bitsStr, 10) > 32) return { ok: false, reason: "/bits > 32 for IPv4" };
    return { ok: true };
  }
  // IPv6: only hex / : / . (last for ::ffff:1.2.3.4 form).
  if (base.includes(":")) {
    if (!/^[0-9a-fA-F:.]+$/.test(base)) return { ok: false, reason: "invalid IPv6 characters" };
    return { ok: true };
  }
  return { ok: false, reason: "not a valid IP or CIDR" };
}

function SecurityTab({
  allowedIps, setAllowedIps, authToken, setAuthToken,
}: {
  allowedIps: string[]; setAllowedIps: (next: string[]) => void;
  authToken: string; setAuthToken: (s: string) => void;
}) {
  // Decouple the toggle state from the values. If we kept enabled
  // implicit on `!!authToken`, you couldn't re-enable the toggle once
  // you'd cleared the field — the empty value would force it off. The
  // *save* side still serializes `authToken === ""` as "no requirement"
  // so a user who toggles on and never types anything ships an empty,
  // open webhook (same as before). Same trick for the IP list.
  const [ipEnabled, setIpEnabled] = useState(allowedIps.length > 0);
  const [tokenEnabled, setTokenEnabled] = useState(!!authToken);
  // Re-sync the toggles when a different webhook loads in.
  useEffect(() => { setIpEnabled(allowedIps.length > 0); }, [allowedIps.length > 0 ? 1 : 0]);
  useEffect(() => { setTokenEnabled(!!authToken); }, [!!authToken]);

  const slots = ipEnabled ? (allowedIps.length === 0 ? [""] : allowedIps) : [];
  const setSlot = (i: number, v: string) => {
    const next = slots.slice();
    next[i] = v;
    setAllowedIps(next);
  };
  const removeSlot = (i: number) => {
    const next = slots.slice();
    next.splice(i, 1);
    setAllowedIps(next.length ? next : [""]);
  };
  const addSlot = () => setAllowedIps([...slots, ""]);

  const genToken = () => {
    const buf = new Uint8Array(32);
    crypto.getRandomValues(buf);
    const hex = Array.from(buf).map((b) => b.toString(16).padStart(2, "0")).join("");
    setAuthToken(hex);
  };
  const copyToken = () => { copyToClipboard(authToken, "Copied bearer token"); };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-sm font-semibold mb-1">Lock down inbound traffic</h3>
        <p className="text-xs muted">
          Both checks are independent. Requests that fail an enabled check are rejected before capture, so they never show up in the Inbox or trigger forwarding.
        </p>
      </div>

      {/* IP allowlist */}
      <section className="space-y-3 border border-border rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Switch
            id="sec-ip-toggle"
            checked={ipEnabled}
            onChange={(on) => {
              setIpEnabled(on);
              setAllowedIps(on ? (allowedIps.length ? allowedIps : [""]) : []);
            }}
            label="Restrict by IP address"
          />
          <label htmlFor="sec-ip-toggle" className="flex-1 cursor-pointer">
            <div className="text-sm font-medium">Restrict by IP address</div>
            <p className="text-xs muted mt-0.5">
              Only accept inbound requests from these IP addresses or CIDR ranges. Read from the <code className="mono">cf-connecting-ip</code> header.
            </p>
          </label>
        </div>
        {ipEnabled && (
          <div className="space-y-2 pt-2 border-t border-border">
            <div className="flex items-center justify-between">
              <label className="label">Allowed IPs <span className="muted">({slots.filter((s) => s.trim()).length})</span></label>
              <button type="button" className="btn text-xs" onClick={addSlot}>+ Add</button>
            </div>
            <p className="text-xs muted">
              Examples: <code className="mono">1.2.3.4</code>, <code className="mono">10.0.0.0/8</code>, <code className="mono">2001:db8::/32</code>.
            </p>
            <div className="space-y-2">
              {slots.map((s, i) => {
                const v = ipValidity(s);
                return (
                  <div key={i} className="flex items-start gap-2">
                    <span className="mono text-[10px] muted-strong w-4 text-right shrink-0 mt-2">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <input
                        className={"input mono w-full " + (v.ok ? "" : "!border-danger")}
                        value={s}
                        onChange={(e) => setSlot(i, e.target.value)}
                        placeholder="1.2.3.0/24"
                        spellCheck={false}
                        aria-invalid={!v.ok}
                      />
                      {!v.ok && (
                        <div className="mt-1 text-[11px] text-danger">{v.reason}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSlot(i)}
                      title="Remove"
                      aria-label="Remove this entry"
                      className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-3 shrink-0"
                    >
                      <CloseIconSmall />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Bearer token */}
      <section className="space-y-3 border border-border rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Switch
            id="sec-token-toggle"
            checked={tokenEnabled}
            onChange={(on) => {
              setTokenEnabled(on);
              if (!on) setAuthToken("");
            }}
            label="Require a bearer token"
          />
          <label htmlFor="sec-token-toggle" className="flex-1 cursor-pointer">
            <div className="text-sm font-medium">Require a bearer token</div>
            <p className="text-xs muted mt-0.5">
              Inbound requests must include <code className="mono">Authorization: Bearer &lt;token&gt;</code>. Comparison is constant-time.
            </p>
          </label>
        </div>
        {tokenEnabled && (
          <div className="space-y-2 pt-2 border-t border-border">
            <label className="label">Bearer token</label>
            <div className="flex items-center gap-2">
              <input
                className="input mono flex-1 min-w-0"
                value={authToken}
                onChange={(e) => setAuthToken(e.target.value)}
                placeholder="(generate one or paste your own)"
                spellCheck={false}
                autoComplete="off"
              />
              <button type="button" className="btn text-xs" onClick={genToken}>Generate</button>
              <button type="button" className="btn text-xs" onClick={copyToken} disabled={!authToken}>Copy</button>
              <button
                type="button"
                className="btn text-xs"
                onClick={() => setAuthToken("")}
                disabled={!authToken}
                title="Clear the token"
              >Clear</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// Compact on/off switch with a sliding thumb. Re-usable beyond this
// tab; kept local for now since the rest of the app uses native
// checkboxes for simple toggles.
function Switch({ id, checked, onChange, label }: {
  id?: string; checked: boolean; onChange: (next: boolean) => void; label: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft mt-0.5 " +
        (checked ? "bg-accent" : "bg-surface-3 border border-border")
      }
    >
      <span
        className={
          "inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform " +
          (checked ? "translate-x-[18px]" : "translate-x-[3px]")
        }
      />
    </button>
  );
}

function CloseIconSmall() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ── Docs tab ───────────────────────────────────────────────────────────

function DocsEditor({ description, setDescription, docs, setDocs }: { description: string; setDescription: (s: string) => void; docs: string; setDocs: (s: string) => void }) {
  const [mode, setMode] = useState<"edit" | "preview" | "split">("split");
  return (
    <div className="space-y-3 min-w-0">
      <div className="min-w-0">
        <label className="label">Short description</label>
        <input className="input mt-1" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="One line — shows up in lists." />
      </div>
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <label className="label">Docs (Markdown)</label>
        <div className="ml-auto flex gap-1">
          {(["edit", "split", "preview"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={
                "px-2 py-0.5 rounded-sm text-xs font-medium " +
                (m === mode ? "bg-accent text-[var(--ink-on-accent)]" : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
              }
            >{m}</button>
          ))}
        </div>
      </div>
      <div className={"flex-1 min-w-0 grid gap-3 " + (mode === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
        {mode !== "preview" && (
          <textarea
            className="input input-mono min-h-[240px] min-w-0 w-full"
            value={docs}
            onChange={(e) => setDocs(e.target.value)}
            placeholder={"## What this webhook does\n\nNotes about the source, expected payload, etc."}
            spellCheck={false}
          />
        )}
        {mode !== "edit" && (
          <div className="rounded border border-border bg-surface-2 p-3 overflow-auto scroll-pane min-h-[240px] min-w-0">
            {docs ? <Markdown source={docs} /> : <p className="text-sm muted italic">Nothing to preview yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function statusClass(code: number): string {
  if (code >= 500) return "bg-danger/15 text-danger";
  if (code >= 400) return "bg-warn/15 text-warn";
  if (code >= 300) return "bg-info/15 text-info";
  return "bg-good/15 text-good";
}

function BoltIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill="currentColor" />
    </svg>
  );
}

// Inline copy button. Flips to a brief "Copied" confirmation, then
// returns. `size="xs"` collapses padding for use inside `.label` rows;
// the default `sm` matches the `.btn` class height so it lines up
// alongside Delete + other primary actions.
function CopyButton({ payload, label, size = "sm" }: { payload: string; label: string; size?: "xs" | "sm" }) {
  const [copied, setCopied] = useState(false);
  // sm: same vertical rhythm as `.btn` (padding: 6px 12px; font: 13px).
  // xs: compact pill for the per-section header copy affordances.
  const sz = size === "xs"
    ? "px-1.5 py-0.5 text-[10px] gap-1"
    : "px-3 py-[6px] text-[13px] gap-1.5";
  const icon = size === "xs" ? 10 : 12;
  return (
    <button
      type="button"
      onClick={async () => {
        const ok = await copyToClipboard(payload, label);
        if (ok) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }
      }}
      className={
        "inline-flex items-center rounded border border-border bg-surface-2 text-text-dim hover:text-text hover:bg-surface-3 transition " +
        sz
      }
      title={label}
      aria-label={label}
    >
      {copied ? (
        <>
          <svg width={icon} height={icon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 12l5 5 9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>Copied</span>
        </>
      ) : (
        <>
          <svg width={icon} height={icon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="1.8" />
          </svg>
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

// Build a faithful curl reproduction of the captured request. Single-
// quoting strings + escaping any embedded single-quotes is the safest
// way to handle JSON bodies and odd header values.
function buildCurl(request: WebhookRequest, url: string): string {
  const sq = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;
  const lines: string[] = [`curl -X ${request.method} ${sq(url)}`];
  for (const [k, v] of request.headers) {
    // Skip headers fetch sets itself or that wouldn't make sense to
    // forward (host, content-length, etc.). Keep the rest so the
    // reproduction is as close to the original as possible.
    if (/^(host|content-length|connection|x-forwarded-|cf-)/i.test(k)) continue;
    lines.push(`  -H ${sq(`${k}: ${v}`)}`);
  }
  if (request.body && request.body.length > 0) {
    lines.push(`  --data-raw ${sq(request.body)}`);
  }
  return lines.join(" \\\n");
}
